import {
  Autocomplete as MuiAutocomplete,
  Box,
  ListSubheader,
  TextField,
  type AutocompleteProps as MuiAutocompleteProps,
  type AutocompleteRenderGroupParams,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { RavenAutocomplete } from "@ravenappdev/raven-ui";
import type { ReactNode } from "react";

export interface AutocompleteProps<T> {
  options: T[];
  value?: T | null;
  inputValue?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  loadingText?: ReactNode;
  noOptionsText?: ReactNode;
  getOptionLabel?: (option: T) => string;
  getOptionDisabled?: (option: T) => boolean;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  onChange?: (value: T | null) => void;
  onInputChange?: (value: string) => void;
  fullWidth?: boolean;
  size?: "small" | "medium";
  sx?: MuiAutocompleteProps<T, false, false, false>["sx"];
  /**
   * Group options under sticky subheaders. MUI requires the `options` array to
   * be pre-sorted by group for this to render contiguous sections.
   */
  groupBy?: (option: T) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
}

// The package renders its own group header (a `Typography` carrying
// `raven-autocomplete__group-header`) whose styling lives in
// `raven-ui.css`, which ESS does not load — so a grouped list would come
// out unstyled and non-sticky. This reproduces MUI's own default group
// rendering, which is what ESS ships today: `styled(ListSubheader)` at
// `top: -8` over the paper background, and a `ul` that indents its
// options by 24px.
const GROUP_LABEL_SX = { backgroundColor: "background.paper", top: -8 } as const;
const GROUP_UL_SX = {
  padding: 0,
  "& .MuiAutocomplete-option": { paddingLeft: "24px" },
} as const;

function renderGroupMuiParity(params: AutocompleteRenderGroupParams) {
  return (
    <li key={params.key}>
      <ListSubheader
        component="div"
        className="MuiAutocomplete-groupLabel"
        sx={GROUP_LABEL_SX}
      >
        {params.group}
      </ListSubheader>
      <Box component="ul" className="MuiAutocomplete-groupUl" sx={GROUP_UL_SX}>
        {params.children}
      </Box>
    </li>
  );
}

export default function Autocomplete<T>({
  options,
  value,
  inputValue,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  readOnly,
  loading,
  loadingText = "Loading…",
  noOptionsText = "No options",
  getOptionLabel,
  getOptionDisabled,
  isOptionEqualToValue,
  onChange,
  onInputChange,
  fullWidth = true,
  size = "small",
  sx,
  groupBy,
  renderGroup,
}: AutocompleteProps<T>) {
  return (
    <RavenAutocomplete<T, false, false, false>
      // Open the dropdown on the first field click. Without this, MUI's
      // default only opens on the popup chevron (or a second click) — the
      // first click just focuses the input, which reads as "the dropdown
      // won't open."
      openOnFocus
      options={options}
      value={value ?? null}
      inputValue={inputValue}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      groupBy={groupBy}
      renderGroup={
        groupBy ? (renderGroup ?? renderGroupMuiParity) : renderGroup
      }
      label={label}
      placeholder={placeholder}
      helperText={helperText}
      error={error}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(_, v) => onChange?.(v)}
      onInputChange={(_, v) => onInputChange?.(v)}
    />
  );
}

// Popper sizing + row rhythm for multi-select dropdowns (CLAUDE.md §8):
// the paper sizes to its longest label without blowing out on mobile, and
// rows match MenuItem's rhythm with a copper tint on the selected ones.
const MULTI_POPPER_SX = (theme: Theme) => ({
  width: "auto !important",
  minWidth: 0,
  "& .MuiAutocomplete-paper": {
    width: "max-content",
    minWidth: "100%",
    maxWidth: "min(440px, calc(100vw - 32px))",
  },
  "& .MuiAutocomplete-listbox": { padding: "4px 0" },
  "& .MuiAutocomplete-option": {
    minHeight: 36,
    paddingTop: "6px",
    paddingBottom: "6px",
  },
  "& .MuiAutocomplete-option[aria-selected='true']": {
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
  },
  "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
    backgroundColor: alpha(theme.palette.warning.main, 0.12),
  },
});

export interface MultiAutocompleteProps<T>
  extends Omit<
    AutocompleteProps<T>,
    "value" | "onChange" | "sx" | "renderGroup"
  > {
  value: T[];
  onChange?: (value: T[]) => void;
  sx?: MuiAutocompleteProps<T, true, false, false>["sx"];
  renderGroup?: MuiAutocompleteProps<T, true, false, false>["renderGroup"];
  /**
   * Bypass MUI's client-side filtering — pass `(o) => o` when the option
   * list is already the answer to the typed query (server-side search).
   */
  filterOptions?: MuiAutocompleteProps<T, true, false, false>["filterOptions"];
}

/**
 * Multi-select sibling of the default export. Selected values render as
 * chips in the field and the menu stays open while picking, so a run of
 * selections costs one interaction each. Kept as its own component rather
 * than a `multiple` flag on `Autocomplete` because the value type differs
 * (`T[]` vs `T | null`) and a discriminated prop set makes every existing
 * single-select call site pay for the generics.
 *
 * Still on MUI rather than `RavenAutocomplete`: the package component has no
 * `slotProps` passthrough, so the §8 popper recipe below — which lives on the
 * portalled Popper, out of reach of the root `sx` — cannot be expressed
 * through it. Swap this over once the package forwards `slotProps`.
 */
export function MultiAutocomplete<T>({
  options,
  value,
  inputValue,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  readOnly,
  loading,
  loadingText = "Loading…",
  noOptionsText = "No options",
  getOptionLabel,
  getOptionDisabled,
  isOptionEqualToValue,
  onChange,
  onInputChange,
  fullWidth = true,
  size = "small",
  sx,
  groupBy,
  renderGroup,
  filterOptions,
}: MultiAutocompleteProps<T>) {
  return (
    <MuiAutocomplete<T, true, false, false>
      multiple
      disableCloseOnSelect
      openOnFocus
      options={options}
      value={value}
      inputValue={inputValue}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      groupBy={groupBy}
      renderGroup={renderGroup}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(_, v) => onChange?.(v as T[])}
      onInputChange={(_, v) => onInputChange?.(v)}
      slotProps={{
        popper: { sx: MULTI_POPPER_SX, placement: "bottom-start" },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
        />
      )}
    />
  );
}
